import './screens.css';
import { el, setText, text } from './walkout.js';
import { STYLES, styleCost } from '../core/styles.js';
import { styleCard } from './styleCard.js';

/**
 * Who you are, before the first fight.
 *
 * Styles have decided how this game is played since step 25 — a loadout is the
 * move list, the stat multipliers *and* the one condition a fighter is allowed
 * to win by — and until step 67 they were picked from a dropdown in the debug
 * panel, unexplained, by somebody who had read the source. A player met them as
 * five words.
 *
 * So the screen's whole job is to **say what a style does before you take it**.
 * Each tile carries the four stat multipliers as the plus and minus they
 * actually are, the moves it teaches, and — the line that matters —
 * `ko.describe`, which is the file's own sentence about the only kind of moment
 * that can finish somebody. Nothing here is invented for the UI: it is
 * `styles.json` read out loud.
 *
 * **One style, and it is permanent.** Until step 70 this screen handed out one
 * to three, which quietly made the most important decision in the game a thing
 * you did in the first thirty seconds with no information — and made the gym's
 * style purchases a way of buying something you could have had for free. So the
 * screen picks exactly one. It is the **primary**: it counts full weight in the
 * stats and it decides the one condition this fighter may ever win by. A second
 * and a third are bought later with development points, they are secondaries,
 * and neither of them moves any of that.
 *
 * The tile is `styleCard.js`, shared with the gym, because choosing a style here
 * and buying one there are the same decision made with different money and a
 * shop that said less than the character creator would be asking for
 * twenty-four points in exchange for a name.
 *
 * It reads and decides nothing: it hands back a name and one style id, and
 * `main.js` puts them on the roster entry through the booking path every other
 * fighter goes through.
 */
export function createCreate({ onDone, onBack, container = document.body }) {
  const root = el('div', 'screen screen--create');
  root.hidden = true;

  root.append(text('div', 'screen__kind', 'a new career'));
  root.append(text('div', 'screen__verdict', 'who walks out'));

  // --- the name -------------------------------------------------------------
  const namePanel = el('div', 'create__name');
  const nameLabel = text('label', 'ui-label', 'fighter name');
  nameLabel.htmlFor = 'create-name';
  const name = el('input', 'create__input');
  name.id = 'create-name';
  name.type = 'text';
  name.maxLength = 22;
  name.placeholder = 'nobody has heard of you yet';
  name.autocomplete = 'off';
  namePanel.append(nameLabel, name);
  root.append(namePanel);

  // --- the styles -----------------------------------------------------------
  const deck = el('div', 'create__styles');
  root.append(deck);

  const tiles = new Map();
  /** The one that was picked, or null. */
  let picked = null;

  for (const id of Object.keys(STYLES.styles)) {
    const tile = styleCard(id, { role: 'primary' });
    tile.addEventListener('click', () => toggle(id));
    deck.append(tile);
    tiles.set(id, tile);
  }

  // --- what that adds up to -------------------------------------------------
  const summary = el('div', 'create__summary');
  const summaryLine = text('div', 'create__summary-line', '');
  const summaryKo = text('div', 'create__summary-ko', '');
  const summaryLater = text('div', 'create__summary-later', '');
  summary.append(summaryLine, summaryKo, summaryLater);
  root.append(summary);

  const doors = el('div', 'create__doors');
  const back = el('button', 'ui-button');
  back.textContent = 'back';
  const go = el('button', 'ui-button');
  go.dataset.tone = 'go';
  go.textContent = 'start climbing';
  doors.append(back, go);
  root.append(doors);
  root.append(
    text('div', 'screen__hint', 'one style. it is permanent — the rest are bought in the gym')
  );

  container.append(root);

  let up = false;

  /**
   * Takes a style. One at a time: picking a second **replaces** the first
   * rather than being refused, because there is no wrong order to undo and a
   * tile that did nothing when clicked would read as broken. Clicking the one
   * already taken gives it back.
   */
  function toggle(id) {
    if (!STYLES.styles[id]) return false;
    picked = picked === id ? null : id;
    draw();
    return true;
  }

  function draw() {
    for (const [id, tile] of tiles) {
      const taken = picked === id;
      tile.dataset.taken = String(taken);
      tile.dataset.primary = String(taken);
      setText(tile.firstChild, taken ? 'primary' : '');
    }

    if (!picked) {
      setText(summaryLine, 'pick one — it is your primary, and it is permanent');
      setText(summaryKo, '');
      setText(summaryLater, '');
    } else {
      const style = STYLES.styles[picked];
      setText(summaryLine, `${style.name} — primary`);
      // The KO condition is the primary's and only the primary's, so this is
      // the one sentence on the screen that is a promise about the whole career.
      setText(summaryKo, `you can only ever finish somebody with ${style.ko.describe}`);
      setText(
        summaryLater,
        `a second style costs ${styleCost(1)} development points and a third ${styleCost(2)} — ` +
          'both are secondaries, and neither changes this'
      );
    }

    go.disabled = !ready();
  }

  const chosenName = () => name.value.trim();
  const ready = () => chosenName().length > 0 && Boolean(picked);

  function show() {
    picked = null;
    name.value = '';
    draw();
    root.hidden = false;
    up = true;
    name.focus();
    return true;
  }

  function confirm() {
    if (!up || !ready()) return null;
    up = false;
    // Focus back before the screen goes, not after. A browser will usually
    // blur a field whose ancestor is hidden, and "usually" is not a thing to
    // rest a fighter's controls on — see `isTyping` in `input.js`.
    name.blur();
    root.hidden = true;
    // A list of one, because everything downstream — `ladder.identify`,
    // `bookFighter`, `createLoadout` — speaks loadouts rather than single
    // styles, and a career grows a second one later.
    const made = Object.freeze({ name: chosenName(), styles: [picked] });
    onDone?.(made);
    return made;
  }

  function leave() {
    if (!up) return false;
    up = false;
    name.blur();
    root.hidden = true;
    onBack?.();
    return true;
  }

  function onKey(event) {
    if (!up) return;
    if (event.key === 'Enter' && ready()) {
      event.preventDefault();
      confirm();
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      leave();
    }
  }

  name.addEventListener('input', draw);
  go.addEventListener('click', confirm);
  back.addEventListener('click', leave);
  window.addEventListener('keydown', onKey);

  return {
    element: root,
    get up() {
      return up;
    },
    /** What is chosen so far. For the tests and a headless run. */
    get choice() {
      return Object.freeze({ name: chosenName(), styles: picked ? [picked] : [] });
    },
    show,
    toggle,
    setName(next) {
      name.value = String(next ?? '');
      draw();
      return name.value;
    },
    confirm,
    dispose() {
      window.removeEventListener('keydown', onKey);
      root.remove();
    },
  };
}
